import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The keyboard and dismissal behaviour every type-ahead list on the site
 * shares (issues 1243, 1250): the header search, the DM screen's "Search to
 * pin…" and the Initiative Tracker's "Add a monster".
 *
 *  • Escape closes the list; a mousedown outside `wrapperRef` closes it too.
 *  • ArrowDown opens a closed list and moves into it; Up/Down then wrap
 *    through the options.
 *  • Enter picks the highlighted option. With `pickTopOnEnter`, Enter with
 *    nothing highlighted picks the first option.
 *
 * The component keeps its own query, results and fetching; it calls `show()`
 * when an answer arrives (which also clears the highlight), spreads
 * `onKeyDown` on its input, and reads `open` / `activeIndex` to render.
 * `count` is how many options the list holds; `onPick(index)` acts on one.
 */
export default function useSuggestList({ count, onPick, pickTopOnEnter = false }) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onMouseDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const show = useCallback(() => {
    setOpen(true);
    setActiveIndex(-1);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        close();
      }
      return;
    }
    if (count === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        setActiveIndex(0);
        return;
      }
      setActiveIndex((prev) => (prev < count - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      if (!open) return;
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : count - 1));
    } else if (e.key === 'Enter') {
      const index = open && activeIndex >= 0 && activeIndex < count ? activeIndex
        : (pickTopOnEnter ? 0 : -1);
      if (index < 0) return;
      e.preventDefault();
      onPick(index);
    }
  };

  return { open, show, close, activeIndex, setActiveIndex, onKeyDown, wrapperRef };
}
